// Sign-in page: Google, or email and password with email verification.
import { useState, type FormEvent } from "react";
import { authErrorMessage } from "../auth/auth";
import { useServices } from "../components/context";
import { GoogleSignInButton } from "../components/GoogleSignInButton";

export function SignIn() {
  const { auth } = useServices();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function attempt(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(authErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === "signup" && password.length < 8) {
      setError("Use a password with at least 8 characters.");
      return;
    }
    void attempt(() =>
      mode === "signin" ? auth.signInWithEmail(email, password) : auth.signUpWithEmail(email, password),
    );
  }

  return (
    <main className="signin">
      <h1>Prego</h1>
      <p className="muted">Private Cargo registry with federated machine identity.</p>
      <div className="card">
        <GoogleSignInButton disabled={busy} onClick={() => void attempt(() => auth.signInWithGoogle())} />
        <p className="divider">or</p>
        <form onSubmit={onSubmit}>
          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              required
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button type="submit" disabled={busy}>
            {mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>
        {error && (
          <p className="banner error" role="alert">
            {error}
          </p>
        )}
        {notice && <p className="banner ok">{notice}</p>}
        <p className="row">
          <button
            type="button"
            className="link"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          >
            {mode === "signin" ? "Create an account" : "I have an account"}
          </button>
          {mode === "signin" && (
            <button
              type="button"
              className="link"
              disabled={busy || !email}
              onClick={() =>
                void attempt(async () => {
                  await auth.resetPassword(email);
                  setNotice("If an account exists for this email, we sent a reset link.");
                })
              }
            >
              Reset password
            </button>
          )}
        </p>
      </div>
    </main>
  );
}
